import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { IMediaTranscoderRepository } from '../../../../../domain/media-transcoder/media-transcoder.repository';
import { ICommonUtilInterface } from '../../../../../domain/common/common-util.interface';
import {
  ETranscodingJobPriority,
  ETranscodingJobStatus,
  TranscoderJobDetails,
} from '../../../../../domain/media-transcoder/transcoder-job';
import {
  EMediaTranscodingItemStatus,
  TranscodingItem,
} from '../../../../../domain/media-transcoder/transcoding-item';
import { MediaTranscodingItem } from '../../../../../domain/media-transcoder/media-transcoding-item';
import {
  BulkRedoSourceEntry,
  TranscodingBulkRedoTracker,
} from '../../../../../domain/media-transcoder/transcoding-bulk-redo-tracker';

const MODAL_ID = 'historyBulkRedoModal';

export interface FailedTranscodingJobRow {
  jobId: string;
  name: string;
  destinationId: number;
  destinationName: string;
  transcodingProfileId: number;
  profileName: string;
  sideEffectName: string;
  sideEffectId: number;
  failedAt: moment.Moment;
  itemStatusDisplay: string;
  selected: boolean;
}

@Component({
  selector: 'evrideo-history-bulk-redo-dialog',
  templateUrl: './history-bulk-redo-dialog.component.html',
  styleUrls: ['./history-bulk-redo-dialog.component.scss'],
})
export class HistoryBulkRedoDialogComponent implements OnInit {
  @Output() completed = new EventEmitter<void>();

  rows: FailedTranscodingJobRow[] = [];
  filteredRows: FailedTranscodingJobRow[] = [];
  isLoading = false;
  isApplying = false;

  nameFilter = '';
  failedAfterFilter = '';
  hideSuccessfullyRedone = true;
  limitToLastBulkAttempt = true;
  filterRevision = 0;
  rangeFrom = 1;
  rangeTo = 1;

  private _jobsWithItemsById = new Map<string, TranscoderJobDetails>();

  constructor(
    private _ngxSmartModalService: NgxSmartModalService,
    private _mediaTranscoderRepository: IMediaTranscoderRepository,
    private _utils: ICommonUtilInterface,
    private _toastr: ToastrService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this._ngxSmartModalService.getModal(MODAL_ID).onOpen.subscribe(() => {
      this._ngZone.run(() => {
        void this._loadFailedJobs();
      });
    });
  }

  get selectedRows(): FailedTranscodingJobRow[] {
    return this.rows.filter((row) => row.selected);
  }

  get selectedCount(): number {
    return this.selectedRows.length;
  }

  get allFilteredSelected(): boolean {
    const visible = this.filteredRows;
    return visible.length > 0 && visible.every((row) => row.selected);
  }

  get lastBulkAttemptCount(): number {
    return TranscodingBulkRedoTracker.getLastBulkAttemptSourceJobIds().size;
  }

  cancel(): void {
    if (this.isApplying) {
      return;
    }
    this._ngxSmartModalService.getModal(MODAL_ID).close();
  }

  toggleSelectAll(): void {
    const select = !this.allFilteredSelected;
    const visibleIds = new Set(this.filteredRows.map((row) => row.jobId));
    this.rows.forEach((row) => {
      if (visibleIds.has(row.jobId)) {
        row.selected = select;
      }
    });
    this._refreshView();
  }

  applyRangeSelection(): void {
    const visible = this.filteredRows;
    if (visible.length === 0) {
      return;
    }

    const from = Math.max(1, Math.min(this.rangeFrom, visible.length));
    const to = Math.max(from, Math.min(this.rangeTo, visible.length));
    const rangeIds = new Set(
      visible.slice(from - 1, to).map((row) => row.jobId),
    );
    this.rows.forEach((row) => {
      row.selected = rangeIds.has(row.jobId);
    });
    this._refreshView();
  }

  toggleRowSelection(row: FailedTranscodingJobRow): void {
    row.selected = !row.selected;
    this._refreshView();
  }

  onFiltersChanged(): void {
    this._recomputeFilteredRows();
    this._refreshView();
  }

  clearFailedAfterFilter(): void {
    this.failedAfterFilter = '';
    this.onFiltersChanged();
  }

  onHideSuccessfullyRedoneChanged(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this._ngZone.run(() => {
      this.hideSuccessfullyRedone = checked;
      this._recomputeFilteredRows();
      this._refreshView();
    });
  }

  onLimitToLastBulkAttemptChanged(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this._ngZone.run(() => {
      this.limitToLastBulkAttempt = checked;
      this._recomputeFilteredRows();
      this._refreshView();
    });
  }

  private _recomputeFilteredRows(): void {
    let result = this.rows;

    if (this.limitToLastBulkAttempt) {
      const lastAttemptIds =
        TranscodingBulkRedoTracker.getLastBulkAttemptSourceJobIds();
      if (lastAttemptIds.size > 0) {
        result = result.filter((row) =>
          lastAttemptIds.has(row.jobId.trim().toLowerCase()),
        );
      }
    }

    const nameQuery = this.nameFilter.trim().toLowerCase();
    if (nameQuery) {
      result = result.filter((row) =>
        row.name.toLowerCase().includes(nameQuery),
      );
    }

    if (this.failedAfterFilter) {
      const cutoff = this._parseFilterCutoff(this.failedAfterFilter);
      if (cutoff) {
        result = result.filter((row) => row.failedAt.isSameOrAfter(cutoff));
      }
    }

    if (this.hideSuccessfullyRedone) {
      result = result.filter(
        (row) =>
          !TranscodingBulkRedoTracker.isSourceJobSuccessfullyRedonePerItem(
            row.jobId,
            this._jobsWithItemsById,
          ),
      );
    }

    this.filteredRows = result;
    this.filterRevision++;
  }

  formatUtc(value: moment.Moment): string {
    return value.format('YYYY-MM-DD HH:mm:ss');
  }

  async apply(): Promise<void> {
    const selected = this.selectedRows;
    if (selected.length === 0 || this.isApplying) {
      return;
    }

    this.isApplying = true;
    this._refreshView();

    TranscodingBulkRedoTracker.recordBulkAttemptSelection(
      selected.map((row) => row.jobId),
    );

    const groups = this._groupSelectedRows(selected);
    let jobsCreated = 0;
    let itemsScheduled = 0;
    let failedBatches = 0;

    try {
      for (const rows of groups.values()) {
        try {
          const buildResult = this._buildBatchedJob(rows);
          if (
            !buildResult?.batch ||
            buildResult.batch.transcodingItems.length === 0
          ) {
            continue;
          }
          const { batch, sources } = buildResult;
          await this._mediaTranscoderRepository.createNewJob(batch);
          TranscodingBulkRedoTracker.record({
            redoJobId: batch.id,
            redoJobName: batch.name,
            sourceJobIds: rows.map((row) => row.jobId),
            sources,
            createdAt: new Date().toISOString(),
          });
          jobsCreated++;
          itemsScheduled += batch.transcodingItems.length;
        } catch {
          failedBatches++;
        }
      }

      if (jobsCreated > 0) {
        const failedSuffix =
          failedBatches > 0 ? ` (${failedBatches} batch(es) failed)` : '';
        this._toastr.success(
          `Created ${jobsCreated} transcoding job(s) with ${itemsScheduled} item(s)${failedSuffix}. Track progress on the Active tab.`,
          'Success',
          {},
        );
        this.completed.emit();
        this._ngxSmartModalService.getModal(MODAL_ID).close();
      } else if (failedBatches > 0) {
        this._toastr.error(
          'Failed to reschedule the selected transcoding jobs.',
          'Error',
          {},
        );
      } else {
        this._toastr.warning(
          'No failed transcoding items were found to reschedule.',
          'Transcoder',
          {},
        );
      }
    } catch {
      this._toastr.error('Failed to reschedule transcoding jobs.', 'Error', {});
    } finally {
      this.isApplying = false;
      this._refreshView();
    }
  }

  private _parseFilterCutoff(localDatetimeValue: string): moment.Moment | null {
    if (!localDatetimeValue?.trim()) {
      return null;
    }
    const parsed = moment.utc(localDatetimeValue);
    return parsed.isValid() ? parsed : null;
  }

  private _refreshView(): void {
    this._changeDetectorRef.markForCheck();
    this._changeDetectorRef.detectChanges();
  }

  private async _loadFailedJobs(): Promise<void> {
    this.isLoading = true;
    this._refreshView();
    this.nameFilter = '';
    this.failedAfterFilter = '';
    this.hideSuccessfullyRedone = true;
    this.limitToLastBulkAttempt = this.lastBulkAttemptCount > 0;
    this.rangeFrom = 1;
    this.rangeTo = 1;

    try {
      const [historyJobs, activeJobs, destinations, profiles, sideEffects] =
        await Promise.all([
          this._mediaTranscoderRepository.getJobs([
            ETranscodingJobStatus.History,
          ]),
          this._mediaTranscoderRepository.getJobs([
            ETranscodingJobStatus.Active,
          ]),
          this._mediaTranscoderRepository.getTenantTranscodingDestinations(),
          this._mediaTranscoderRepository.getProfiles(),
          this._mediaTranscoderRepository.getSideEffects(),
        ]);

      this._indexJobsWithItems([...historyJobs, ...activeJobs]);

      const jobs = historyJobs;

      const destinationNames = Object.fromEntries(
        destinations.map((d) => [d.id, d.name]),
      );
      const profileNames = Object.fromEntries(
        profiles.map((p) => [p.id, p.name]),
      );
      const sideEffectNames = Object.fromEntries(
        sideEffects.map((s) => [s.id, s.name]),
      );

      const failedJobs = jobs.filter((job) =>
        job.itemStatuses?.includes(EMediaTranscodingItemStatus.Failed),
      );

      this.rows = failedJobs.map((job) => {
        const sideEffectId = job.sideEffectId ?? 0;
        return {
          jobId: job.id,
          name: job.name,
          destinationId: job.destinationId,
          destinationName: destinationNames[job.destinationId] ?? '—',
          transcodingProfileId: job.transcodingProfileId,
          profileName: profileNames[job.transcodingProfileId] ?? '—',
          sideEffectName:
            sideEffectId > 0
              ? (sideEffectNames[sideEffectId] ?? String(sideEffectId))
              : '—',
          sideEffectId,
          failedAt: job.actualEndTime,
          itemStatusDisplay: this._formatItemStatuses(job.itemStatuses),
          selected: false,
        };
      });

      this.rangeTo = Math.max(1, this.rows.length);
      this._recomputeFilteredRows();
    } catch {
      this.rows = [];
      this.filteredRows = [];
      this._toastr.error('Failed to load failed transcoding jobs.', 'Error', {});
    } finally {
      this.isLoading = false;
      this._refreshView();
    }
  }

  private _formatItemStatuses(
    statuses: EMediaTranscodingItemStatus[] | undefined,
  ): string {
    if (!statuses?.length) {
      return '';
    }
    return statuses
      .map((s) => EMediaTranscodingItemStatus[s])
      .filter((name) => !!name)
      .join(', ');
  }

  private _groupSelectedRows(
    selected: FailedTranscodingJobRow[],
  ): Map<string, FailedTranscodingJobRow[]> {
    const groups = new Map<string, FailedTranscodingJobRow[]>();
    for (const row of selected) {
      const key = this._batchGroupKey(row);
      const bucket = groups.get(key);
      if (bucket) {
        bucket.push(row);
      } else {
        groups.set(key, [row]);
      }
    }
    return groups;
  }

  private _batchGroupKey(row: FailedTranscodingJobRow): string {
    return `${row.destinationId}|${row.transcodingProfileId}|${row.sideEffectId}`;
  }

  private _indexJobsWithItems(jobs: TranscoderJobDetails[]): void {
    this._jobsWithItemsById.clear();
    for (const job of jobs) {
      this._jobsWithItemsById.set(job.id.trim().toLowerCase(), job);
    }
  }

  private _buildBatchedJob(
    rows: FailedTranscodingJobRow[],
  ): {
    batch: TranscoderJobDetails;
    sources: BulkRedoSourceEntry[];
  } | null {
    const seenItems = new Set<string>();
    const mergedItems: TranscodingItem[] = [];
    const mediaIdsBySourceJob = new Map<string, number[]>();

    for (const row of rows) {
      const sourceJob = this._jobsWithItemsById.get(
        row.jobId.trim().toLowerCase(),
      );
      if (!sourceJob?.transcodingItems?.length) {
        continue;
      }

      for (const item of sourceJob.transcodingItems) {
        if (item.status !== EMediaTranscodingItemStatus.Failed) {
          continue;
        }
        const dedupeKey = this._transcodingItemDedupeKey(item);
        if (seenItems.has(dedupeKey)) {
          continue;
        }
        seenItems.add(dedupeKey);
        mergedItems.push(this._cloneItemAsPending(item));

        if (item instanceof MediaTranscodingItem) {
          const mediaIds = mediaIdsBySourceJob.get(row.jobId) ?? [];
          mediaIds.push(item.mediaId);
          mediaIdsBySourceJob.set(row.jobId, mediaIds);
        }
      }
    }

    if (mergedItems.length === 0) {
      return null;
    }

    const sources: BulkRedoSourceEntry[] = rows.map((row) => ({
      sourceJobId: row.jobId,
      mediaIds: [...new Set(mediaIdsBySourceJob.get(row.jobId) ?? [])],
    }));

    const template = rows[0];
    const batch = new TranscoderJobDetails(
      this._utils.generateGuid(),
      '',
      template.destinationId,
      ETranscodingJobPriority.Low,
      ETranscodingJobStatus.Active,
      null,
      null,
      null,
      null,
      mergedItems,
      template.transcodingProfileId,
      template.sideEffectId,
    );

    return { batch, sources };
  }

  private _cloneItemAsPending(item: TranscodingItem): TranscodingItem {
    if (item instanceof MediaTranscodingItem) {
      return new MediaTranscodingItem(
        this._utils.generateGuid(),
        item.mediaId,
        item.videoTitle,
        item.videoTenantUniqueId,
        item.videoDurationMs,
        item.startTimeMs,
        item.endTimeMs,
        EMediaTranscodingItemStatus.Pending,
        '',
        null,
        '',
      );
    }

    return item.clone(this._utils);
  }

  private _transcodingItemDedupeKey(item: TranscodingItem): string {
    if (item instanceof MediaTranscodingItem) {
      return `m:${item.mediaId}:${item.startTimeMs}:${item.endTimeMs}`;
    }
    return `i:${item.id}:${item.startTimeMs}:${item.endTimeMs}`;
  }

}
